import {
  Dropdown,
  Text,
  IDropdownOption,
  Stack,
  ActionButton,
  IconButton,
  Callout,
  DirectionalHint,
  TextField,
  DefaultButton,
} from "@fluentui/react";
import { styles } from "./filterComponent.styles";
import { useCallback, useMemo, useRef, useState } from "react";
import debounce from "lodash/debounce";

interface DurationFilterProps {
  /**
   * Label for the filter, e.g. "Created", "Modified", etc.
   */
  text: string;
  /**
   * Currently selected duration option. If undefined, it means no filter is applied.
   */
  selection?: DurationOption;
  /**
   * Default increment for quick selections and custom input. Defaults to "day".
   */
  increment?: DurationIncrement;
  /**
   * Whether the filter can be cleared. If true, a clear button will appear when a selection is made.
   */
  clearable?: boolean;
  /**
   * Operator to use for the filter. Defaults to ">=". This determines how the duration is compared (e.g., "Created >= 7 days ago").
   */
  selectedOperator?: Operator;
  /**
   * Predefined quick selection options for the filter. Each option includes a duration and its corresponding increment. If not provided, defaults to 1, 10, 30, and 90 days.
   */
  quickSelections?: { duration: number; increment: DurationIncrement }[];
  /**
   * Callback function that is called when the duration or operator changes. It receives the new duration, increment, and operator as parameters. If duration is undefined, it means the filter has been cleared.
   */
  onChange?: (
    duration: number | undefined,
    increment: DurationIncrement,
    operator: Operator,
  ) => void;
}

export interface DurationOption {
  duration: number;
  increment: DurationIncrement;
  operator: Operator;
}

export enum DurationIncrement {
  Second = "second",
  Minute = "minute",
  Day = "day",
  Week = "week",
  Month = "month",
  Year = "year",
}

/**
 * DurationFilter that allows users to filter items based on a duration relative to the current time (e.g., "Created >= 7 days ago"). It supports both quick selections and custom input, as well as different operators for comparison.
 */
const DurationFilter = ({
  text,
  selection,
  increment = DurationIncrement.Day,
  clearable,
  selectedOperator = ">=",
  quickSelections = defaultQuickSelections,
  onChange,
}: DurationFilterProps) => {
  const [isCalloutVisible, setIsCalloutVisible] = useState(false);
  const [customDuration, setCustomDuration] = useState<string>("");
  const [localOperator, setLocalOperator] =
    useState<Operator>(selectedOperator);
  const buttonRef = useRef<HTMLDivElement>(null);

  const displayText = useMemo(() => {
    if (selection) {
      const { duration, increment, operator } = selection;
      return `${text} ${operator} ${duration} ${increment}${duration > 1 ? "s" : ""}`;
    }
    return text;
  }, [selection, text]);

  const handleOperatorChange = (
    _: React.FormEvent<HTMLDivElement>,
    option?: IDropdownOption,
  ) => {
    if (!option) return;
    const newOperator = option.key as Operator;
    setLocalOperator(newOperator);
    if (onChange && selection?.duration) {
      onChange(
        selection.duration,
        selection.increment || increment,
        newOperator,
      );
    }
  };

  const handleDurationChange = useCallback(
    (duration: number, inc: DurationIncrement) => {
      if (onChange) {
        onChange(duration, inc, localOperator);
      }
    },
    [onChange, localOperator],
  );

  const debouncedDurationChange = useMemo(
    () =>
      debounce((value: string, inc: DurationIncrement) => {
        const parsed = Number(value);
        if (parsed > 0) {
          handleDurationChange(parsed, inc);
        }
      }, 500),
    [handleDurationChange],
  );

  const handleClear = () => {
    setLocalOperator(">=");
    setCustomDuration("");
    debouncedDurationChange.cancel();
    if (onChange) {
      onChange(undefined, increment, ">=");
    }
  };

  return (
    <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 4 }}>
      <Stack
        horizontal
        verticalAlign="center"
        className={styles.dateFilterButtonContainer}
      >
        <div ref={buttonRef}>
          <ActionButton
            text={displayText}
            iconProps={{ iconName: "Timer" }}
            onClick={() => setIsCalloutVisible(!isCalloutVisible)}
            styles={{
              root: styles.dateFilterButtonOperator,
              textContainer: styles.dateFilterButtonText,
            }}
          />
        </div>

        {clearable && selection && (
          <IconButton
            iconProps={{ iconName: "Clear", style: { fontSize: 12 } }}
            title="Clear filter"
            onClick={handleClear}
            className={styles.clearButton}
          />
        )}
      </Stack>

      {isCalloutVisible && buttonRef.current && (
        <Callout
          target={buttonRef.current}
          onDismiss={() => setIsCalloutVisible(false)}
          directionalHint={DirectionalHint.bottomLeftEdge}
          isBeakVisible={false}
        >
          <Stack
            tokens={{ childrenGap: 12 }}
            styles={{ root: styles.calloutContent }}
          >
            <Stack tokens={{ childrenGap: 8 }}>
              <Text
                style={{
                  fontWeight: 500,
                }}
              >
                Operator:
              </Text>
              <Dropdown
                options={operatorOptions}
                selectedKey={localOperator}
                onChange={handleOperatorChange}
                styles={{ root: styles.operatorDropdown }}
              />
            </Stack>
            <Stack
              tokens={{
                childrenGap: 5,
              }}
            >
              <Text
                style={{
                  fontWeight: 500,
                }}
              >
                Quick Selection
              </Text>
              {quickSelections.map((duration) => (
                <DefaultButton
                  key={duration.duration}
                  text={`${duration.duration} ${duration.increment}${duration.duration > 1 ? "s" : ""}`}
                  onClick={() => {
                    handleDurationChange(duration.duration, duration.increment);
                    setCustomDuration("");
                  }}
                  checked={
                    selection?.duration === duration.duration &&
                    selection?.increment === duration.increment &&
                    customDuration === ""
                  }
                />
              ))}
              <TextField
                placeholder="Custom"
                type="number"
                min={1}
                suffix={`${increment}s`}
                value={customDuration}
                onChange={(_, newValue) => {
                  setCustomDuration(newValue || "");
                  debouncedDurationChange(newValue || "", increment);
                }}
              />
            </Stack>
          </Stack>
        </Callout>
      )}
    </Stack>
  );
};

export type Operator = ">" | ">=" | "<" | "<=" | "=" | "!=";

const operatorOptions: IDropdownOption[] = [
  { key: ">", text: "greater than (>)" },
  { key: ">=", text: "greater than or equal (>=)" },
  { key: "<", text: "less than (<)" },
  { key: "<=", text: "less than or equal (<=)" },
  { key: "=", text: "equal to (=)" },
  { key: "!=", text: "not equal to (!=)" },
];

const defaultQuickSelections = [
  {
    duration: 1,
    increment: DurationIncrement.Day,
  },
  {
    duration: 10,
    increment: DurationIncrement.Day,
  },
  {
    duration: 30,
    increment: DurationIncrement.Day,
  },
  {
    duration: 90,
    increment: DurationIncrement.Day,
  },
];

export default DurationFilter;
